import { useState } from "react"
import Layout from "@/pages/layouts/application"
import { usePage, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { PageHeader } from "@/components/core/page_header"
import { FormField } from "@/components/core/form_field"
import { ChevronDown } from "lucide-react"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import * as api from "@/api"

function IntegrationCard({ logo, name, description, connected, onConnect, onDisconnect }) {
  return (
    <div className="flex flex-col gap-3 p-4 border rounded-md">
      <div className="flex items-center gap-1.5">
        <img src={logo} alt={name} className="size-5 shrink-0" />
        <span className="font-medium text-sm">{name}</span>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="mt-auto">
        {connected ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <span className="size-2 rounded-full bg-green-500" />
                <span>Connected</span>
                <ChevronDown className="size-3 transition-transform" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0.5" align="start">
              <button
                onClick={onDisconnect}
                className="text-xs text-red-400 hover:text-red-500 transition-colors px-2 py-1"
              >
                Disconnect
              </button>
            </PopoverContent>
          </Popover>
        ) : (
          <Button size="sm" variant="secondary" onClick={onConnect}>
            Connect
          </Button>
        )}
      </div>
    </div>
  )
}

export default function Show() {
  const { integrations, auth } = usePage().props
  const { organization } = auth

  const { readwise, twitter, linkedin, slack, calendar } = integrations
  const [showReadwiseForm, setShowReadwiseForm] = useState(false)

  const { data, setData, post, delete: destroy, transform, processing, errors } = useForm({
    access_token: "",
  })

  const onConnect = (event, type) => {
    event.preventDefault()

    transform(data => ({
      integration: data
    }))

    post(api.organizationsIntegrationsConnection.create.path({ org_nano_id: organization.nano_id, type }))
  }

  const onDisconnect = (event, type) => {
    event.preventDefault()

    destroy(api.organizationsIntegrationsConnection.destroy.path({ org_nano_id: organization.nano_id, type }))
  }

  return (
    <Layout>
      <PageHeader title="Integrations" />
      <div className="grid grid-cols-3 gap-3">
        <IntegrationCard
          logo="/linkedin.png"
          name="LinkedIn"
          description="Publish posts to your LinkedIn profile, and add first comments."
          connected={linkedin?.connected}
          onConnect={() => document.getElementById("linkedin-oauth").click()}
          onDisconnect={(e) => onDisconnect(e, "Linkedin")}
        />

        <IntegrationCard
          logo="/twitter.png"
          name="Twitter"
          description="Cross-post your content to X."
          connected={twitter?.connected}
          onConnect={() => document.getElementById("twitter-oauth").click()}
          onDisconnect={(e) => onDisconnect(e, "Twitter")}
        />

        <IntegrationCard
          logo="/slack.png"
          name="Slack"
          description="Create posts directly from Slack via text and voice notes."
          connected={slack?.connected}
          onConnect={() => document.getElementById("slack-oauth").click()}
          onDisconnect={(e) => onDisconnect(e, "Slack")}
        />

        <IntegrationCard
          logo="/google-calendar.png"
          name="Google Calendar"
          description="Auto-generate posts from your meeting transcripts."
          connected={calendar?.connected}
          onConnect={() => window.location.href = api.organizationsCalendar.connect.path({ org_nano_id: organization.nano_id }) + "?platform=google"}
          onDisconnect={() => destroy(api.organizationsCalendar.disconnect.path({ org_nano_id: organization.nano_id }))}
        />

        

        {/* Readwise  */}
        <div className="flex flex-col gap-3 p-4 border rounded-md">
          <div className="flex items-center gap-1.5">
            <img src="/readwise.png" alt="Readwise" className="size-5 rounded-sm shrink-0" />
            <span className="font-medium text-sm">Readwise</span>
          </div>
          <p className="text-xs text-muted-foreground">Create content from your Kindle highlights. Get your token {" "}
                <a
                  href="https://readwise.io/access_token"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-primary inline-flex items-center gap-1"
                >
                  here
                </a>
                .
              </p>

          {readwise?.connected ? (
            <div className="mt-auto">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    <span className="size-2 rounded-full bg-green-500" />
                    <span>Connected</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="start">
                  <button
                    onClick={(e) => onDisconnect(e, "Readwise")}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors px-2 py-1"
                  >
                    Disconnect
                  </button>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className="mt-auto">
              <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] space-y-2`} style={{ height: showReadwiseForm ? "auto" : "32px", transitionDuration: showReadwiseForm ? "300ms" : "300ms" }}>
                {!showReadwiseForm ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowReadwiseForm(true)}
                  >
                    Connect
                  </Button>
                ) : (
                  <form onSubmit={(event) => onConnect(event, "Readwise")} className="space-y-2">
                    <div className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${showReadwiseForm ? "delay-100" : ""}`} style={{ transform: showReadwiseForm ? "translateY(0)" : "translateY(20px)", opacity: showReadwiseForm ? 1 : 0 }}>
                      <FormField
                        name="readwise"
                        type="text"
                        value={data.access_token}
                        placeholder="Paste access token"
                        onChange={(e) => setData("access_token", e.target.value)}
                        errors={errors}
                      />
                    </div>
                    <div className={`flex items-center gap-2 overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${showReadwiseForm ? "delay-150" : ""}`} style={{ transform: showReadwiseForm ? "translateY(0)" : "translateY(20px)", opacity: showReadwiseForm ? 1 : 0 }}>
                      <Button
                        type="submit"
                        size="sm"
                        variant="secondary"
                        disabled={processing || !data.access_token}
                      >
                        {processing ? "Connecting..." : "Connect"}
                      </Button>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowReadwiseForm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
